import { SlTitledListComponent } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import { isPositiveValue } from 'features/accounts/AccountDetails/AccountDetails.helpers';
import usePayOverTimeHistoryDetails from 'features/payOverTime/screens/ManageLoans/hooks/usePayOverTimeHistoryDetails';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import TransactionsSectionList from 'shared/components/TransactionSectionList/TransactionSectionList';
import HairLine from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { amountStyles } from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const HistoryDetails = () => {
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'planDetails',
  });

  const { loanId } = useLocalSearchParams<{ loanId: string }>();

  const {
    resultsObj: {
      loanName,
      fee,
      originationDate,
      principal,
      totalAmount,
      transactions,
    },
    error,
    fetching,
  } = usePayOverTimeHistoryDetails(loanId);

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const mapToHistoryTransactionSectionList = (
    section: SlTitledListComponent,
  ) => {
    return {
      title: section.title?.copy || '',
      transactions: section.children.map((transaction) => {
        return {
          titleText: transaction.primaryText?.copy || '',
          secondaryText: transaction.secondaryText?.copy || '',
          amountText: transaction.primaryDetail?.copy || '',
          balanceText: transaction.secondaryDetail?.copy || '',
          secondaryDetail: transaction.secondaryDetail?.copy || '',
          isPending: false,
          amountStyle: isPositiveValue(
            transaction.primaryDetail.copy || '',
          ) as amountStyles,
        };
      }),
    };
  };

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={navigateBack} />
          ),
        }}
      />
      {transactions?.length && transactions?.length > 0 ? (
        <ScrollView style={styles.content}>
          <View>
            <TitleMdText style={styles.title}>
              {translatedText('planDetails')}
            </TitleMdText>
            <View style={styles.planAmountContainer}>
              <BodySmText style={styles.planAmount} numberOfLines={0}>
                {translatedText('planAmount')}
              </BodySmText>
              <ExpressiveLgText style={styles.expressiveText} numberOfLines={0}>
                {principal?.formattedWithPlusPrefix}
              </ExpressiveLgText>
            </View>
            <ListItem primaryCopy={loanName ?? ''} showsChevron={false} />
            <View style={styles.hairlineWrapper}>
              <HairLine />
            </View>
            <ListItem
              primaryCopy={translatedText('fee')}
              showsChevron={false}
              trailingAccessory={{
                id: 'feeAmount',
                name: VXLComponentId.LIST_ITEM_TEXT_STACK,
                props: {
                  primaryCopy: fee?.formattedWithPlusPrefix ?? '',
                },
              }}
            />
            <View style={styles.hairlineWrapper}>
              <HairLine />
            </View>
            <ListItem
              primaryCopy={translatedText('total')}
              showsChevron={false}
              trailingAccessory={{
                id: 'totalAmount',
                name: VXLComponentId.LIST_ITEM_TEXT_STACK,
                props: {
                  primaryCopy: totalAmount?.formattedWithPlusPrefix ?? '',
                },
              }}
            />
            <View style={styles.hairlineWrapper}>
              <HairLine />
            </View>
            <ListItem
              primaryCopy={translatedText('planDate')}
              showsChevron={false}
              trailingAccessory={{
                id: 'planDate',
                name: VXLComponentId.LIST_ITEM_TEXT_STACK,
                props: {
                  primaryCopy: originationDate ?? '',
                },
              }}
            />
          </View>
          <View style={styles.sectionDivider} />
          <TransactionsSectionList
            transactions={transactions.map(mapToHistoryTransactionSectionList)}
            scrollEnabled={false}
          />
        </ScrollView>
      ) : (
        <View style={styles.emptyStateContainer}>
          <View style={styles.image}>
            <Illustration illustration="COINS_C" />
          </View>
          <BodyBaseText>{translatedText('emptyHistory')}</BodyBaseText>
        </View>
      )}

      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            // ToDo: Replace later with BNPL specific error screen
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error.title')}
              messageBody={translatedText('error.body')}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: goHome,
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  emptyStateContainer: {
    alignItems: 'center',
    flex: 1,
  },
  hairlineWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  image: {
    height: 131,
    width: 131,
  },
  planAmount: {
    color: theme.color.text.secondary,
  },
  expressiveText: {
    ...Platform.select({
      android: {
        paddingTop: 0,
      },
      ios: {
        paddingTop: theme.spacing.xs,
      },
    }),
  },
  planAmountContainer: {
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  title: {
    padding: theme.spacing.m,
  },
});

export default HistoryDetails;
